import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

/**
 * Service responsible for fetching and caching country and state data
 * from static JSON assets.
 */
@Injectable({ providedIn: 'root' })
export class CountryService {
  private countries: Array<{ label: string; value: string; hasZipCode: boolean }> = [];

  constructor(private http: HttpClient) {}

  /**
   * Returns all countries. Uses cache if already loaded.
   * @returns Observable of formatted countries
   */
  getCountries(): Observable<any[]> {
    if (this.countries.length > 0) {
      return of(this.countries);
    }
    return this.http.get<any>('assets/countries/countries.json').pipe(
      map((res: any) => {
        this.countries = res.country.map((c: any) => ({
          label: c.name,
          value: c.code,
          hasZipCode: c.hasZipCode === true || c.hasZipCode === 'true',
        }));
        return this.countries;
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Returns the cached countries array (without API call).
   */
  getCachedCountries(): any[] {
    return this.countries;
  }

  /**
   * Returns all states for a given country code.
   * @param code Country ISO code
   * @returns Observable of states (label + value)
   */
  getStates(code: string): Observable<any[]> {
    if (!code) return of([]);
    return this.http.get<any>(`assets/countries/${code}.json`).pipe(
      map((res: any) =>
        res.states?.state?.map((s: any) => ({
          label: s.name,
          value: s.code,
        })) || []
      ),
      catchError(() => of([]))
    );
  }
}
